// Integration endpoints (admin JWT). Shapes mirror the API exactly:
//   - Integration   → api/src/services/integration-service.ts (Integration)
//   - the routes     → api/src/http/routes/integrations.ts (list/create/test/delete)
//
// A BYO messaging integration binds a customer-owned Telegram/Discord/WhatsApp bot
// to a Cogent channel: the org supplies the bot credentials, we relay both ways.
//
// RELAY-GATED (like channels): the routes are mounted ONLY when the control plane
// has a relay link configured. Without it, GET /admin/integrations is an un-mounted
// route and Hono's notFound returns a BARE 404 (no JSON envelope) — a soft
// "integrations unavailable" state the screen renders distinctly (see
// `isRelayUnavailable`), separate from a real enveloped 404/400.
//
// Pattern: a thin data source over `apiFetch` (vault: RemoteDataSource /
// Asynchronous_Data_Loading — each call returns a Promise<T> the caller awaits),
// with the success body runtime-validated at the boundary (vault Type_Assertion)
// so envelope drift fails loud as a ContractError instead of an undefined deref.
import { z } from "zod";
import { apiFetch, ApiError } from "./client";

export type IntegrationProvider = "telegram" | "discord" | "whatsapp";
export type IntegrationStatus = "active" | "invalid" | "unverified";

export interface Integration {
  id: string;
  orgId: string;
  provider: IntegrationProvider;
  /** Cogent channel id this bot relays to (Channel.id from api/channels). */
  channelId: string;
  channelName: string;
  /** Provider-scoped tenant key the relay routes inbound webhooks by. */
  tenantKey: string;
  status: IntegrationStatus;
  /** Masked hint for the stored secret (e.g. "…4567"), or null when none. */
  credHint: string | null;
  createdAt: string;
  /** Last time the credentials were verified against the provider, or null. */
  lastVerifiedAt: string | null;
}

const integrationSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  provider: z.enum(["telegram", "discord", "whatsapp"]),
  channelId: z.string(),
  channelName: z.string(),
  tenantKey: z.string(),
  status: z.enum(["active", "invalid", "unverified"]),
  credHint: z.string().nullable(),
  createdAt: z.string(),
  lastVerifiedAt: z.string().nullable(),
}) satisfies z.ZodType<Integration>;

const integrationsResponseSchema = z.object({ integrations: z.array(integrationSchema) });

export interface CreateIntegrationInput {
  provider: IntegrationProvider;
  /** Cogent channel id the bot binds to. */
  channelId: string;
  /** The Cogent channel's join secret — the relay verifies it, never stored here. */
  channelSecret: string;
  /** Org_ID for Team channels; omit for free (password-only) channels. */
  orgId?: string;
  /** Provider credentials keyed by the registry field names (providers.ts). */
  credentials: Record<string, string>;
}

export interface TestConnectionInput {
  provider: IntegrationProvider;
  credentials: Record<string, string>;
}

export interface TestConnectionResult {
  ok: boolean;
  detail?: string;
}

const testResultSchema = z.object({
  ok: z.boolean(),
  detail: z.string().optional(),
}) satisfies z.ZodType<TestConnectionResult>;

export async function listIntegrations(signal?: AbortSignal): Promise<Integration[]> {
  const res = await apiFetch("/admin/integrations", { auth: true, signal, schema: integrationsResponseSchema });
  return res.integrations;
}

export function createIntegration(body: CreateIntegrationInput): Promise<Integration> {
  return apiFetch("/admin/integrations", { method: "POST", auth: true, body, schema: integrationSchema });
}

/**
 * Validate provider credentials without persisting (no integration is created).
 * The relay pings the provider (e.g. Telegram getMe) and reports back `{ ok, detail }`.
 */
export function testConnection(body: TestConnectionInput): Promise<TestConnectionResult> {
  return apiFetch("/admin/integrations/test", { method: "POST", auth: true, body, schema: testResultSchema });
}

export function deleteIntegration(id: string): Promise<void> {
  return apiFetch(`/admin/integrations/${encodeURIComponent(id)}`, { method: "DELETE", auth: true });
}

/**
 * True when the feature itself is unavailable because the control plane has no
 * relay link — the routes aren't mounted, so the app returns a bare 404 with no
 * error envelope (code `HTTP_404`). Deliberately distinct from a real enveloped
 * 404 (e.g. SESSION_NOT_FOUND) so the screen can show "not enabled yet" rather
 * than a scary failure.
 */
export function isRelayUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404 && err.code === "HTTP_404";
}
